<template>
    <div
        class="d-flex"
        data-test-role="pagination-tabs-container">
        <div
            v-bind:class="{ invisible: hidePrev }"
            class="d-flex flex-column px-2 justify-content-center">
            <button
                id="HeaderBackBtn"
                class="bg-transparent border-0"
                v-on:click.prevent="prev">
                <span class="visually-hidden">Previous page</span>
                <div class="page-prev-icon" />
            </button>
        </div>
        <div
            ref="tabContainer"
            class="w-100"
            style="overflow: hidden">
            <div
                v-bind:style="containerStyle"
                class="d-flex">
                <div
                    v-for="page in pagesWithVis"
                    v-bind:key="page.PageNumber"
                    v-bind:style="paginationStyle"
                    class="p-2">
                    <tab
                        v-bind:page="page"
                        v-bind:current-page-number="currentPageNumber"
                        v-bind:apply-hover-style="true"
                        v-bind:data-visible="page.visible"
                        v-on:click="pageClicked(page)" />
                </div>
            </div>
        </div>
        <div
            v-bind:class="{ invisible: hideNext }"
            class="d-flex flex-column px-2 justify-content-center">
            <button
                id="HeaderNextBtn"
                class="bg-transparent border-0"
                v-on:click.prevent="next">
                <span class="visually-hidden">Next page</span>
                <div class="page-next-icon" />
            </button>
        </div>
    </div>
</template>

<script>
    import tab from "../paginationTab.vue";
    import { BP } from "Responsive/breakpoint";
    import { responsiveManager, isAtLeast } from "Responsive/manager";

    export default {
        components: { tab },
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            pages: { type: Array, required: true },
            currentPageNumber: { type: Number, required: false, default: 0 }
        },
        emits: ["page-clicked"],
        data: function () {
            return {
                scrollOffset: 0,
                containerWidth: 0
            };
        },
        computed: {
            maxTabs() {
                if (isAtLeast(this.viewport, BP.xl)) {
                    return 5;
                }
                if (isAtLeast(this.viewport, BP.lg)) {
                    return 4;
                }
                if (isAtLeast(this.viewport, BP.md)) {
                    return 3;
                }
                if (isAtLeast(this.viewport, BP.sm)) {
                    return 2;
                }
                return 1;
            },
            numDisplayedTabs() {
                return Math.min(this.pages.length, this.maxTabs);
            },
            tabWidth() {
                if (this.containerWidth <= 0) {
                    return 0;
                }
                const tabWidth = this.containerWidth / this.numDisplayedTabs;
                return tabWidth;
            },
            paginationStyle() {
                const tabWidthPx = `${this.tabWidth}px`;
                return {
                    width: tabWidthPx,
                    "min-width": tabWidthPx
                };
            },
            containerStyle() {
                const marginLeft = -this.scrollOffset * this.tabWidth;
                return {
                    width: `${this.containerWidth}px`,
                    "margin-left": `${marginLeft}px`,
                    transition: "margin-left .4s"
                };
            },
            pagesWithVis() {
                const leftmostTab = this.scrollOffset;
                const rightmostTab = this.scrollOffset + this.numDisplayedTabs - 1;
                return this.pages.map((p, i) => {
                    const visible = leftmostTab <= i && i <= rightmostTab ? "visible" : "hidden";
                    return {
                        ...p,
                        visible
                    };
                });
            },
            maxScrollOffset() {
                return this.pages.length - this.maxTabs;
            },
            hideNext() {
                return this.scrollOffset >= this.maxScrollOffset;
            },
            hidePrev() {
                return this.scrollOffset <= 0;
            },
            globalWidth() {
                return responsiveManager.globalWidth.value;
            }
        },
        watch: {
            globalWidth() {
                this.calcTabContainerWidth();
                this.calcScrollOffsetForPageNumber(this.currentPageNumber);
            },
            currentPageNumber(value) {
                this.calcScrollOffsetForPageNumber(value);
            }
        },
        mounted() {
            this.$nextTick(() => {
                this.calcTabContainerWidth();
            });
        },
        methods: {
            calcTabContainerWidth() {
                this.containerWidth = this.$refs.tabContainer.getBoundingClientRect().width;
            },
            pageClicked(page) {
                this.$emit("page-clicked", page);
            },
            getBoundedOffset(offset) {
                return Math.min(Math.max(offset, 0), this.maxScrollOffset);
            },
            calcScrollOffsetForPageNumber(pageNumber) {
                if (this.numDisplayedTabs < this.maxTabs) {
                    this.scrollOffset = 0;
                    return;
                }
                // Note: page number is 1 indexed
                // calculate the page initially in the center
                const initialCenter = Math.ceil(this.maxTabs / 2);
                this.scrollOffset = this.getBoundedOffset(pageNumber - initialCenter);
            },
            prev() {
                this.scrollOffset = this.getBoundedOffset(this.scrollOffset - 1);
            },
            next() {
                this.scrollOffset = this.getBoundedOffset(this.scrollOffset + 1);
            }
        }
    };
</script>
<style lang="scss" scoped>
    $form-pagination-arrow-size: 1.25rem;

    .page-next-icon,
    .page-prev-icon {
        background-image: url("Images/icons/sideArrow.svg");
        width: $form-pagination-arrow-size;
        height: $form-pagination-arrow-size;
        background-size: $form-pagination-arrow-size $form-pagination-arrow-size;
        //filter: grayscale(100%) brightness(5) brightness(0.7);
    }
    .page-prev-icon {
        transform: rotate(180deg);
    }
</style>
